import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import type { State } from '@helpcenter/common-state-selectors';
import { getThemeColor } from '@helpcenter/common-state-selectors/src/help-center';
import { isLoggedIn as isUserLoggedIn } from '@helpcenter/common-state-selectors/src/user';
import { SkeletonItem } from '@helpcenter/skeleton-item';
import { di } from 'react-magnetic-di';
import { useExternalLink } from 'view/external-links/use-external-link';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { IconTile } from '@atlaskit/icon';
import type { NewCoreIconProps } from '@atlaskit/icon';
import QuestionsIcon from '@atlaskit/icon/core/comment';
import ActivityIcon from '@atlaskit/icon/core/dashboard';
import DocumentIcon from '@atlaskit/icon/core/page';
import ScreenIcon from '@atlaskit/icon/core/screen';
import { Box, xcss } from '@atlaskit/primitives';
import { generateThemeBranding } from '@atlassian/csm-colour-theme-generator';
import { isCSMHelpCenter } from '@atlassian/help-center-common-util/advanced-help-center';
import { getEnv } from '@atlassian/help-center-common-util/env';
import { IntegrationProviderMeta as ThirdPartyProvider } from '@atlassian/third-party-connectors/src/common/constants';
import type { HelpObjectStoreArticleSourceSystem } from '../inline-article-item/__generated__/inlineArticleItemFragmentV2.graphql';

export interface Props {
    entityType: string;
    displayLink: string;
    isExternal?: boolean;
    isLoggedIn: boolean;
    themeColor: string;
    iconUrl?: string | null;
    sourceSystem?: HelpObjectStoreArticleSourceSystem | null;
}

export const ResourceIcon = ({
    entityType,
    displayLink,
    isLoggedIn,
    isExternal,
    themeColor,
    iconUrl,
    sourceSystem,
}: Props) => {
    di(useExternalLink);
    const {
        data: { iconUrl: externalResourceIconUrl },
        isLoading: isIconLoading,
    } = useExternalLink({
        isLoggedIn,
        link: displayLink,
    });

    const isValidResourceIconUrl = !!iconUrl;
    const isValidExternalResourceIconUrl = !!externalResourceIconUrl && externalResourceIconUrl !== '';
    const getDefaultIcon = useMemo(() => {
        switch (entityType) {
            case 'ARTICLE':
                return <DocumentIcon label="" testId="article-icon" spacing="spacious" />;

            case 'REQUEST_FORM':
                return <ActivityIcon label="" testId="request-form-icon" spacing="spacious" />;

            case 'PORTAL':
                return <ScreenIcon label="" testId="portal-icon" spacing="spacious" />;

            default:
                return <QuestionsIcon label="" testId="question-icon" spacing="spacious" />;
        }
    }, [entityType]);

    const getThirdPartyIcon = useMemo(() => {
        if (sourceSystem === 'SHAREPOINT') return <ThirdPartyProvider.sharepoint.icon label="sharepoint" />;
        if (sourceSystem === 'GOOGLE_DRIVE') return <ThirdPartyProvider.googleDrive.icon label="googledrive" />;
        return null;
    }, [sourceSystem]);
    const getThirdPartyIconComponent = useMemo(() => {
        if (sourceSystem === 'SHAREPOINT') return ThirdPartyProvider.sharepoint.icon;
        if (sourceSystem === 'GOOGLE_DRIVE') return ThirdPartyProvider.googleDrive.icon;
        return null;
    }, [sourceSystem]);
    const shouldUseArticleIconTile =
        FeatureGates.checkGate('jsm_kb_team_26_changes') &&
        entityType === 'ARTICLE' &&
        !isExternal &&
        !isIconLoading &&
        !isValidResourceIconUrl;

    const getExternalResourceIcon = () => {
        if (isIconLoading) {
            return <SkeletonItem height={'24px'} width={'24px'} />;
        }

        if (isValidExternalResourceIconUrl) {
            return (
                <Box as="img" xcss={imgStyles} testId="external-resource-icon" src={externalResourceIconUrl} alt="" />
            );
        }

        return getDefaultIcon;
    };

    const getResourceIcon = () => {
        if (isIconLoading) {
            return <SkeletonItem height={'24px'} width={'24px'} />;
        }
        if (isValidResourceIconUrl) {
            return <Box as="img" xcss={imgStyles} testId="resource-icon" src={iconUrl} alt="" />;
        }

        return getThirdPartyIcon ?? getDefaultIcon;
    };

    if (shouldUseArticleIconTile) {
        const ThirdPartyIconInTile = ({ label }: NewCoreIconProps) => {
            if (getThirdPartyIcon) {
                return getThirdPartyIcon;
            }
            return <DocumentIcon label={label} />;
        };
        const IconComponent = getThirdPartyIconComponent ? ThirdPartyIconInTile : DocumentIcon;
        return (
            <Box xcss={iconTileStyles}>
                <IconTile
                    appearance="blue"
                    icon={IconComponent}
                    label="Article icon"
                    size="medium"
                    testId="article-icon"
                />
            </Box>
        );
    }

    return (
        <Box
            xcss={imgStyles}
            style={{
                color: isCSMHelpCenter(getEnv().helpCenterType)
                    ? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
                      generateThemeBranding(themeColor).color900
                    : themeColor,
            }}
        >
            {isExternal ? getExternalResourceIcon() : getResourceIcon()}
        </Box>
    );
};

const ConnectedResourceIcon = connect((state: State) => {
    return {
        isLoggedIn: isUserLoggedIn(state),
        themeColor: getThemeColor(state),
    };
})(ResourceIcon);

export default ConnectedResourceIcon;

const imgStyles = xcss({
    width: 'size.200',
    height: 'size.200',
    objectFit: 'contain',
});

const iconTileStyles = xcss({
    width: 'size.300',
    height: 'size.300',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});
